import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom"; // Ajout de useNavigate et useLocation
import SafeeLogo from "../assets/svg/safee-logo.svg"; // Import the Safee logo
import { useAuth } from "../contexts/auth-context"; // Import du hook d'auth

interface LoginFormProps {
  onSubmit: (credentials: {
    email: string;
    password: string;
  }) => Promise<boolean>;
  error?: string | null;
  loading?: boolean;
}

const LoginPage: React.FC<LoginFormProps> = ({
  onSubmit,
  error,
  loading = false,
}) => {
  // Utiliser sessionStorage pour persister l'email même en cas de re-render
  const [email, setEmail] = useState(() => {
    return sessionStorage.getItem("login_email") || "";
  });
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithUserData } = useAuth();
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Sauvegarder l'email dans sessionStorage à chaque changement
  React.useEffect(() => {
    if (email) {
      sessionStorage.setItem("login_email", email);
    }
  }, [email]);

  // Gestion du token dans la query string
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (token) {
      setTokenLoading(true);
      loginWithUserData(token)
        .then(() => {
          setTokenLoading(false);
          navigate("/");
        })
        .catch(() => {
          setTokenLoading(false);
          setTokenError("Token de connexion invalide ou expiré.");
        });
    }
  }, [location.search, loginWithUserData, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setFormError("Veuillez remplir tous les champs");
      return;
    }

    setFormError(null);

    try {
      const success = await onSubmit({ email, password });

      if (success) {
        // Effacer l'email stocké après une connexion réussie
        sessionStorage.removeItem("login_email");
        navigate("/"); // Use React Router instead of window.location.href
      } else {
        // En cas d'échec, réinitialiser seulement le mot de passe
        setPassword("");
      }
    } catch (err) {
      console.error("💥 LoginPage: Error during login submission:", err);
      setFormError("Une erreur est survenue lors de la connexion");
      // En cas d'erreur, réinitialiser seulement le mot de passe
      setPassword("");
    }
  };

  return (
    <Container maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            height: "100vh",
            flexGrow: 1,
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: 20,
          }}
        >
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', backgroundColor: '#004366', p: 5, borderRadius: '50%' }}>
            <img src={SafeeLogo} alt="Safee Logo" style={{ width: '150px', height: '150px' }} />
          </Box>

          <Typography component="h1" variant="h3" sx={{ fontWeight: 'bold' }}>
            Backoffice
          </Typography>

          {tokenLoading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Connexion automatique en cours...
            </Alert>
          )}
          {tokenError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {tokenError}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 3, width: "100%" }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Adresse email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Mot de passe"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />

            {(error || formError) && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error || formError}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </Box>
        </Box>
    </Container>
  );
};

export default LoginPage;
